<template>
  <div class="mop-field">
    <label>Materiales</label>
    <p v-if="loading" class="mop-hint">Cargando el catálogo de materiales…</p>
    <div v-else-if="hasLoadFailed" class="mop-alert" role="alert">
      <span>No pudimos cargar el catálogo de materiales. Cierra sesión y vuelve a ingresar al portal para pedir materiales.</span>
      <button type="button" class="mop-retry" @click="loadCatalog">Reintentar</button>
    </div>
    <div v-else-if="catalog" class="mop-catalog">
      <p v-if="!groups.length" class="mop-hint">Tu cocina todavía no tiene materiales en el catálogo.</p>
      <materials-order-group
        v-for="group in layout.mainGroups"
        :key="group.key"
        :group="group"
        :selection="value"
        :max-quantity="catalog.max_quantity"
        :quick-buttons="quickButtons"
        @change="changeQuantity"
      />
      <button
        v-if="isToggleVisible"
        type="button"
        class="mop-toggle"
        :aria-expanded="String(isExpanded)"
        @click="toggleOtherGroups"
      >{{ toggleLabel }}</button>
      <materials-order-group
        v-for="group in visibleOthers"
        :key="group.key"
        :group="group"
        :selection="value"
        :max-quantity="catalog.max_quantity"
        :quick-buttons="quickButtons"
        @change="changeQuantity"
      />
    </div>
  </div>
</template>

<script>
import PackagingCatalogGateway from '@/gateways/PackagingCatalogGateway';
import MaterialsOrderGroup from './MaterialsOrderGroup.vue';
import { isQuickButtonsEnabled } from './materialsOrderBoxes';
import { groupCatalogItems, presetsForItem, withQuantity } from './materialsOrderSelection';
import {
  groupKeysWithSelection,
  hiddenItemCount,
  isOthersToggleVisible,
  otherGroupsToggleLabel,
  splitGroupsByLine,
  visibleOtherGroups,
  withGroupKey,
} from './materialsOrderLayout';

export default {
  name: 'MaterialsOrderPicker',
  components: { MaterialsOrderGroup },
  props: {
    value: { type: Object, default: () => ({}) },
    businessLine: { type: String, default: '' },
  },
  data() {
    return {
      catalog: null,
      loading: false,
      hasLoadFailed: false,
      isExpanded: false,
      keptGroupKeys: [],
    };
  },
  computed: {
    quickButtons() {
      return isQuickButtonsEnabled(this.catalog);
    },
    groups() {
      if (!this.catalog) return [];
      const items = this.catalog.items.map((item) => ({ ...item, presets: presetsForItem(item, this.catalog.presets) }));
      return groupCatalogItems(items, this.businessLine);
    },
    layout() {
      return splitGroupsByLine(this.groups, this.businessLine);
    },
    visibleOthers() {
      return visibleOtherGroups({
        otherGroups: this.layout.otherGroups,
        isExpanded: this.isExpanded,
        keptGroupKeys: this.keptGroupKeys,
        selection: this.value,
      });
    },
    hiddenCount() {
      return hiddenItemCount(this.layout.otherGroups, this.visibleOthers);
    },
    isToggleVisible() {
      return isOthersToggleVisible({ otherGroups: this.layout.otherGroups, isExpanded: this.isExpanded, hiddenCount: this.hiddenCount });
    },
    toggleLabel() {
      return otherGroupsToggleLabel(this.isExpanded, this.hiddenCount);
    },
  },
  created() {
    this.loadCatalog();
  },
  methods: {
    async loadCatalog() {
      this.loading = true;
      this.hasLoadFailed = false;
      try {
        this.catalog = await PackagingCatalogGateway.fetchCatalog();
        this.$emit('catalog-loaded', this.catalog.items);
      } catch (loadError) {
        this.catalog = null;
        this.hasLoadFailed = true;
      } finally {
        this.loading = false;
      }
    },
    toggleOtherGroups() {
      this.keptGroupKeys = groupKeysWithSelection(this.layout.otherGroups, this.value);
      this.isExpanded = !this.isExpanded;
    },
    changeQuantity({ groupKey, materialId, quantity }) {
      this.keptGroupKeys = withGroupKey(this.keptGroupKeys, groupKey);
      this.$emit('input', withQuantity(this.value, materialId, quantity));
    },
  },
};
</script>

<style scoped>
.mop-field { margin-bottom: 16px; }
.mop-field > label {
  font-weight: 600;
  font-size: 12.5px;
  color: #374151;
  margin-bottom: 6px;
  display: block;
}
.mop-hint {
  margin: 0;
  font-size: 13px;
  color: #6b7280;
}
.mop-alert {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 13px;
  background: #fef2f2;
  color: #b91c1c;
  border-left: 3px solid #b91c1c;
}
.mop-retry {
  border: none;
  background: transparent;
  color: #01662E;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  padding: 0;
  font-family: inherit;
}
.mop-catalog {
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  padding: 4px 14px 12px;
}
.mop-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 10px 0 0;
  padding: 6px 0;
  border: none;
  background: transparent;
  color: #01662E;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
}
.mop-toggle::after {
  content: '';
  width: 7px;
  height: 7px;
  border-right: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  transform: translateY(-2px) rotate(45deg);
}
.mop-toggle[aria-expanded="true"]::after { transform: translateY(2px) rotate(-135deg); }
.mop-toggle:hover { text-decoration: underline; }
.mop-toggle:focus-visible {
  outline: 2px solid #01662E;
  outline-offset: 2px;
  border-radius: 6px;
}
</style>
